import { asyncRouterMap, constantRouterMap } from '@/router/index';

// 判断是否有权限访问该菜单
function hasPermission(menus, route) {
  // 添加全面的空值检查
  if (!route || typeof route !== 'object') {
    return false;
  }
  
  if (route.name) {
    let currMenu = getMenu(route.name, menus);
    if (currMenu != null) {
      // 设置菜单的标题、图标和可见性
      // 确保 meta 对象存在
      route.meta = route.meta || {};
      
      if (currMenu.title != null && currMenu.title !== '') {
        route.meta.title = currMenu.title;
      }
      if (currMenu.icon != null && currMenu.icon !== '') {
        route.meta.icon = currMenu.icon;
      }
      if (currMenu.hidden != null) {
        route.hidden = currMenu.hidden !== 0;
      }
      if (currMenu.sort != null && currMenu.sort !== '') {
        route.sort = currMenu.sort;
      }
      return true;
    } else {
      route.sort = route.sort || 0;
      if (route.hidden !== undefined && route.hidden === true) {
        route.sort = -1;
        return true;
      } else {
        return false;
      }
    }
  } else {
    // 没有 name 的路由默认允许访问
    return true;
  }
}

// 根据路由名称获取菜单
function getMenu(name, menus) {
  // 添加全面的空值检查
  if (!name || !menus || !Array.isArray(menus)) {
    return null;
  }
  
  for (let i = 0; i < menus.length; i++) {
    let menu = menus[i];
    if (menu && menu.name === name) {
      return menu;
    }
  }
  return null;
}

// 对菜单进行排序
function sortRouters(accessedRouters) {
  // 添加全面的空值检查
  if (!accessedRouters || !Array.isArray(accessedRouters)) {
    return;
  }
  
  // 使用安全的遍历方式
  for (let i = 0; i < accessedRouters.length; i++) {
    let router = accessedRouters[i];
    if (router && router.children && Array.isArray(router.children) && router.children.length > 0) {
      // 使用安全的排序
      router.children = router.children.filter(child => child != null).sort(compare("sort"));
    }
  }
  
  // 过滤掉 null 值后排序
  const validRouters = accessedRouters.filter(router => router != null);
  validRouters.sort(compare("sort"));
  
  // 清空原数组并重新填充
  accessedRouters.length = 0;
  accessedRouters.push(...validRouters);
}

// 降序比较函数
function compare(property) {
  return function(a, b) {
    // 添加全面的空值检查
    if (!a || !b) return 0;
    const valueA = a[property] !== undefined ? a[property] : 0;
    const valueB = b[property] !== undefined ? b[property] : 0;
    return valueB - valueA;
  }
}

// 清理路由配置，确保数据安全
function sanitizeRoute(route) {
  if (!route || typeof route !== 'object') {
    return null;
  }
  
  return {
    ...route,
    meta: route.meta || {},
    props: route.props || {},
    children: Array.isArray(route.children) ? route.children.map(child => sanitizeRoute(child)).filter(Boolean) : []
  };
}

const permission = {
  state: {
    routers: Array.isArray(constantRouterMap) ? constantRouterMap : [],
    addRouters: []
  },
  mutations: {
    SET_ROUTERS: (state, routers) => {
      // 确保 routers 是安全的数组
      const safeRouters = Array.isArray(routers) ? routers.map(route => sanitizeRoute(route)).filter(Boolean) : [];
      state.addRouters = safeRouters;
      state.routers = (Array.isArray(constantRouterMap) ? constantRouterMap : []).concat(safeRouters);
    }
  },
  actions: {
    GenerateRoutes({ commit }, data) {
      return new Promise((resolve) => {
        try {
          //console.log('GenerateRoutes called with data:', data);
          
          // 处理各种边界情况
          if (!data || typeof data !== 'object') {
            console.warn('GenerateRoutes: Invalid data provided');
            commit('SET_ROUTERS', []);
            resolve();
            return;
          }

          const { menus, username } = data;
          
          // 确保 menus 是有效的数组
          const menuList = Array.isArray(menus) ? menus : [];
          // console.log('Processing menus:', menuList);

          // 确保 asyncRouterMap 存在且是数组
          if (!asyncRouterMap || !Array.isArray(asyncRouterMap)) {
            console.error('asyncRouterMap is not defined or not an array');
            commit('SET_ROUTERS', []);
            resolve();
            return;
          }

          // 管理员特殊处理（如果需要）
          // if (username === 'admin') {
          //   console.log('Admin user detected, granting all permissions');
          //   const adminRouters = asyncRouterMap.map(route => sanitizeRoute(route)).filter(Boolean);
          //   sortRouters(adminRouters);
          //   commit('SET_ROUTERS', adminRouters);
          //   resolve();
          //   return;
          // }

          // 过滤可访问的路由 - 使用更安全的方式
          const accessedRouters = asyncRouterMap
            .map(route => sanitizeRoute(route))
            .filter(route => {
              if (!route) return false;
              
              try {
                const hasAccess = hasPermission(menuList, route);
                //console.log(`Route ${route.name || 'unnamed'} access:`, hasAccess);
                
                if (hasAccess && route.children && route.children.length > 0) {
                  // 过滤子路由
                  route.children = route.children
                    .map(child => sanitizeRoute(child))
                    .filter(child => child && hasPermission(menuList, child));
                  
                  // 如果子路由全部被过滤掉，且父路由没有独立用途，则隐藏父路由
                  if (route.children.length === 0 && !route.alwaysShow && !(route.meta && route.meta.alwaysShow)) {
                    return false;
                  }
                }
                
                return hasAccess;
              } catch (error) {
                console.error('Error checking route permission:', error, route);
                return false;
              }
            });

          // console.log('Filtered accessedRouters:', accessedRouters);
          
          // 对菜单进行排序
          sortRouters(accessedRouters);
          commit('SET_ROUTERS', accessedRouters);
          resolve();
        } catch (error) {
          console.error('GenerateRoutes error:', error);
          // 出错时设置空路由，避免页面崩溃
          commit('SET_ROUTERS', []);
          resolve();
        }
      });
    }
  }
};

export default permission;


// WEBPACK FOOTER //
// ./src/store/modules/permission.js